import { IonContent, IonPage } from '@ionic/react';
import { useHistory, Redirect } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/useAuth';
import { getMyPublications, archivePublication } from '@/lib/api/publications';
import { Logo } from '@/components/ui/Logo';

const KIND_LABEL: Record<string, string> = {
  article: 'Article', essay: 'Essay', book: 'Book', series: 'Series',
};

const VISIBILITY_STYLES: Record<string, string> = {
  public: 'bg-green-50 text-green-700',
  unlisted: 'bg-yellow-50 text-yellow-700',
  private: 'bg-gray-100 text-gray-500',
};

export default function PublicationsPage() {
  const { status } = useAuth();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { data: publications = [], isLoading } = useQuery({
    queryKey: ['my-publications'],
    queryFn: getMyPublications,
    enabled: status === 'authenticated',
  });

  const archiveMutation = useMutation({
    mutationFn: archivePublication,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-publications'] }),
  });

  if (status === 'unauthenticated') return <Redirect to="/auth" />;
  if (status === 'needs-handle') return <Redirect to="/auth/claim-handle" />;

  function confirmArchive(id: string, title: string) {
    if (window.confirm(`Archive "${title}"? It will no longer be visible to readers.`)) {
      archiveMutation.mutate(id);
    }
  }

  return (
    <IonPage>
      <IonContent scrollY fullscreen>
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
          <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-3">
            <button
              onClick={() => history.goBack()}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <Logo size="sm" />
            <span className="text-sm font-semibold text-gray-700">My publications</span>
            <div className="ml-auto">
              <button
                onClick={() => history.push('/publications/new')}
                className="rounded-lg bg-[#2f3e46] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33]"
              >
                New publication
              </button>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-3xl px-5 py-8 pb-20">
          {isLoading && (
            <div className="flex justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
            </div>
          )}

          {!isLoading && publications.length === 0 && (
            <div className="flex flex-col items-center gap-4 py-20 text-center">
              <p className="text-gray-500">You haven't created any publications yet.</p>
              <button
                onClick={() => history.push('/publications/new')}
                className="rounded-lg bg-[#2f3e46] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33]"
              >
                Create your first publication
              </button>
            </div>
          )}

          <div className="flex flex-col divide-y divide-gray-100">
            {publications.map(pub => (
              <div key={pub.id} className="flex items-start justify-between gap-4 py-4">
                <div
                  className="flex min-w-0 flex-1 cursor-pointer flex-col gap-1"
                  onClick={() => history.push(`/publications/${pub.id}`)}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[0.67rem] font-extrabold uppercase tracking-widest text-[#1a56a0]">
                      {KIND_LABEL[pub.kind] ?? pub.kind}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-medium ${VISIBILITY_STYLES[pub.visibility] ?? 'bg-gray-100 text-gray-500'}`}>
                      {pub.visibility}
                    </span>
                  </div>
                  <h3 className="font-serif text-lg font-bold leading-snug text-gray-900 truncate transition-colors hover:text-[#1a56a0]">
                    {pub.title}
                  </h3>
                  <p className="text-xs text-gray-400">
                    {new Date(pub.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    &nbsp;·&nbsp;/{pub.slug}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={e => { e.stopPropagation(); history.push(`/publications/${pub.id}/edit`); }}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); confirmArchive(pub.id, pub.title); }}
                    disabled={archiveMutation.isPending}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-red-500 transition-colors hover:bg-red-50 disabled:opacity-40"
                  >
                    Archive
                  </button>
                </div>
              </div>
            ))}
          </div>
        </main>
      </IonContent>
    </IonPage>
  );
}
